(() => {
  class MosesCleanerPage extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        if (this.proofVisibilityHandler) {
          document.addEventListener('visibilitychange', this.proofVisibilityHandler);
          this.proofMotion?.addEventListener('change', this.proofVisibilityHandler);
        }
        this.galleryObserver?.observe(this.querySelector('[data-gallery-open]'));
        this.startProofRotation();
        return;
      }
      this.initialized = true;
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
      this.image = this.querySelector('[data-gallery-main]');
      this.large = this.querySelector('[data-gallery-large]');
      this.dialog = this.querySelector('[data-gallery-dialog]');
      this.active = 0;
      this.setupProofCarousel();
      this.thumbViewport = this.querySelector('.mcl-thumbnails');
      const gallery = this.querySelector('.mcl-gallery');
      const mainButton = this.querySelector('[data-gallery-open]');
      const fitThumbnailRail = () => gallery.style.setProperty('--mcl-gallery-height', `${mainButton.getBoundingClientRect().height}px`);
      fitThumbnailRail();
      this.galleryObserver = new ResizeObserver(fitThumbnailRail);
      this.galleryObserver.observe(mainButton);
      this.querySelector('[data-gallery-scroll]').addEventListener('click', () => {
        const rail = this.thumbViewport;
        const atEnd = rail.scrollTop + rail.clientHeight >= rail.scrollHeight - 2;
        rail.scrollTo({ top: atEnd ? 0 : rail.scrollTop + rail.clientHeight * .85, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      });
      this.thumbs.forEach((button, index) => button.addEventListener('click', () => this.select(index)));
      this.querySelectorAll('[data-gallery-prev]').forEach(button => button.addEventListener('click', () => this.select(this.active - 1)));
      this.querySelectorAll('[data-gallery-next]').forEach(button => button.addEventListener('click', () => this.select(this.active + 1)));
      this.querySelector('[data-gallery-open]')?.addEventListener('click', () => this.dialog.showModal());
      this.querySelector('[data-gallery-close]')?.addEventListener('click', () => this.dialog.close());
      this.dialog?.addEventListener('click', event => { if (event.target === this.dialog) this.dialog.close(); });
      this.addEventListener('keydown', event => {
        if (!(this.dialog?.open || event.target.closest('.mcl-gallery'))) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.select(this.active + (event.key === 'ArrowRight' ? 1 : -1));
        }
      });
      this.querySelectorAll('a[href^="#mcl-offer-"]').forEach(link => link.addEventListener('click', event => {
        const target = this.querySelector('.mcl-product');
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        target.focus({ preventScroll: true });
      }));
      this.querySelector('[data-purchase-form]')?.addEventListener('submit', event => { event.preventDefault(); this.addToCart(); });
      this.querySelectorAll('.mcl-plan input').forEach(input => input.addEventListener('change', () => {
        const error = this.querySelector('[data-purchase-error]');
        if (error) error.hidden = true;
        this.querySelectorAll('[data-plan-details]').forEach(panel => { panel.hidden = panel.dataset.planDetails !== input.value; });
      }));
    }
    setupProofCarousel() {
      this.proof = this.querySelector('[data-proof-carousel]');
      this.proofSlides = [...this.querySelectorAll('[data-proof-slide]')];
      if (!this.proof || this.proofSlides.length < 2) return;
      this.proofIndex = 0;
      this.proofMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.proof.addEventListener('mouseenter', () => { this.proofHover = true; this.startProofRotation(); });
      this.proof.addEventListener('mouseleave', () => { this.proofHover = false; this.startProofRotation(); });
      this.proof.addEventListener('focusin', () => { this.proofFocus = true; this.startProofRotation(); });
      this.proof.addEventListener('focusout', event => {
        this.proofFocus = this.proof.contains(event.relatedTarget);
        this.startProofRotation();
      });
      this.proofVisibilityHandler = () => this.startProofRotation();
      document.addEventListener('visibilitychange', this.proofVisibilityHandler);
      this.proofMotion.addEventListener('change', this.proofVisibilityHandler);
      this.startProofRotation();
    }
    startProofRotation() {
      clearInterval(this.proofTimer);
      if (!this.proofSlides?.length || !this.isConnected) return;
      if (this.proofHover || this.proofFocus || this.proofMotion.matches || document.hidden) return;
      this.proofTimer = setInterval(() => this.showProof(this.proofIndex + 1), 6000);
    }
    showProof(index) {
      this.proofIndex = (index + this.proofSlides.length) % this.proofSlides.length;
      this.proofSlides.forEach((slide, i) => { slide.hidden = i !== this.proofIndex; });
    }
    disconnectedCallback() {
      clearInterval(this.proofTimer);
      this.galleryObserver?.disconnect();
      document.removeEventListener('visibilitychange', this.proofVisibilityHandler);
      this.proofMotion?.removeEventListener('change', this.proofVisibilityHandler);
    }
    select(index) {
      this.active = (index + this.thumbs.length) % this.thumbs.length;
      const selected = this.thumbs[this.active];
      const artwork = selected.querySelector('.mcl-gallery-art');
      const main = artwork.cloneNode(true);
      const expanded = artwork.cloneNode(true);
      this.querySelector('[data-gallery-open]').replaceChildren(main);
      this.large.replaceChildren(expanded);
      this.thumbs.forEach((button, i) => button.setAttribute('aria-pressed', String(i === this.active)));
      this.querySelectorAll('[data-gallery-counter]').forEach(counter => counter.textContent = `Image ${this.active + 1} of ${this.thumbs.length}`);
      this.thumbViewport.scrollTo({ top: selected.offsetTop - (this.thumbViewport.clientHeight - selected.offsetHeight) / 2, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      this.querySelector('[data-gallery-open]').setAttribute('aria-label', `Enlarge image: ${selected.dataset.imageAlt}`);
    }
    async addToCart() {
      if (this.busy || this.dataset.available !== 'true') return;
      const button = this.querySelector('[data-add-to-cart]');
      const error = this.querySelector('[data-purchase-error]');
      const plan = this.querySelector('.mcl-plan input:checked')?.dataset.sellingPlan;
      const payload = { id: Number(this.dataset.variantId), quantity: 1 };
      if (plan) payload.selling_plan = Number(plan);
      const gifts = (this.dataset.complimentary || '').split(',').map(Number).filter(id => id > 0 && id !== payload.id);
      this.busy = true;
      button.disabled = true;
      button.textContent = 'Adding…';
      error.hidden = true;
      try {
        const cart = window.Alpine?.store?.('cart');
        if (cart) await cart.add(payload, plan ? gifts : []);
        else {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [payload, ...(plan ? gifts.map(id => ({ id, quantity: 1, parent_id: payload.id })) : [])] })
          });
          if (!response.ok) throw new Error((await response.json()).description || 'Unable to add to cart. Please try again.');
          window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
        }
      } catch (err) {
        error.textContent = err.message || 'Unable to add to cart. Please try again.';
        error.hidden = false;
      } finally {
        this.busy = false;
        button.disabled = false;
        button.textContent = 'Add to cart';
      }
    }
  }
  if (!customElements.get('moses-cleaner-page')) customElements.define('moses-cleaner-page', MosesCleanerPage);
})();
